import React, { useState, useCallback, useRef } from 'react';
import { TurnstileInstance } from '@marsidev/react-turnstile';
import InputSection from './InputSection';
import OutputSection from './OutputSection';
import ErrorMessage from './ErrorMessage';
import { transformText } from '../services/api';
import { validateInput } from '../utils/constants';
import { AppState } from '../types';

const TextTransformer: React.FC = () => {
  const [state, setState] = useState<AppState>({
    inputText: '',
    originalText: '',
    transformedText: '',
    isLoading: false,
    error: null,
    isFromCache: false,
    provider: undefined,
  });
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);

  const handleInputChange = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      inputText: value,
      error: null,
    }));
  }, []);

  const handleTransform = useCallback(async () => {
    const validationError = validateInput(state.inputText);
    if (validationError) {
      setState(prev => ({
        ...prev,
        error: validationError,
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      originalText: prev.inputText,
    }));

    try {
      const response = await transformText(state.inputText, turnstileToken);

      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          transformedText: response.data!.transformedText,
          isFromCache: response.data!.fromCache,
          provider: response.data!.provider,
          isLoading: false,
        }));
      } else {
        setState(prev => ({
          ...prev,
          error: response.error || 'Failed to transform text',
          isLoading: false,
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'An unexpected error occurred. Please try again.',
        isLoading: false,
      }));
    } finally {
      setTurnstileToken('');
      turnstileRef.current?.reset();
    }
  }, [state.inputText, turnstileToken]);

  const handleRetry = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
    }));
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 min-h-screen flex flex-col">
      <div className="text-center mb-4">
        <div className="relative inline-block">
          <h1 className="text-4xl md:text-6xl font-black text-neo-black mb-2 transform -rotate-1 relative z-10">
            <span className="bg-neo-yellow px-4 py-1 border-4 border-neo-black shadow-neo-lg inline-block transform rotate-2">
              THAROORIFY
            </span>
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold text-neo-black transform rotate-1 relative -mt-3">
            <span className="bg-neo-pink px-3 py-1 border-3 border-neo-black shadow-neo inline-block">
              Your English
            </span>
          </h2>

          {/* Decorative elements */}
          <div className="absolute -top-4 -left-6 w-8 h-8 bg-neo-blue border-3 border-neo-black transform rotate-45 hidden md:block"></div>
          <div className="absolute -bottom-2 -right-4 w-6 h-6 bg-neo-green border-2 border-neo-black transform -rotate-12 hidden md:block"></div>
          <div className="absolute top-8 -right-14 text-4xl hidden md:block">✨</div>
          <div className="absolute -top-6 left-1/2 text-2xl hidden md:block">🎭</div>
        </div>

        <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mt-4 font-medium leading-snug">
          Transform your everyday English into
          <span className="bg-neo-purple px-2 py-0 border-2 border-neo-black font-bold text-neo-black mx-1">
            sophisticated
          </span>
          , polished prose with the
          <span className="bg-neo-green px-2 py-0 border-2 border-neo-black font-bold text-neo-black mx-1">
            eloquent
          </span>
          <span className="bg-neo-blue px-2 py-0 border-2 border-neo-black font-bold text-neo-black mx-1">
            literary
          </span>
          excellence of Shashi Tharoor.
        </p>

        <div className="flex items-center justify-center mt-3 space-x-4">
          <div className="bg-neo-white border-3 border-neo-black px-3 py-1 shadow-neo-sm">
            <span className="text-sm font-bold text-gray-600">📚 Powered by AI Magic</span>
          </div>
          <div className="bg-neo-white border-3 border-neo-black px-3 py-1 shadow-neo-sm">
            <span className="text-sm font-bold text-gray-600">⚡ Instant Results</span>
          </div>
        </div>
      </div>

      <div className="bg-neo-white border-4 border-neo-black shadow-neo-lg p-4 flex-1 flex flex-col min-h-0">
        <InputSection
          value={state.inputText}
          onChange={handleInputChange}
          onTransform={handleTransform}
          isLoading={state.isLoading}
          error={state.error}
          turnstileToken={turnstileToken}
          onTurnstileToken={setTurnstileToken}
          turnstileRef={turnstileRef}
        />

        {state.error && (
          <div className="mt-4">
            <ErrorMessage
              message={state.error}
              onRetry={handleRetry}
            />
          </div>
        )}

        <OutputSection
          originalText={state.originalText}
          transformedText={state.transformedText}
          isFromCache={state.isFromCache}
          isLoading={state.isLoading}
          provider={state.provider}
        />
      </div>
    </div>
  );
};

export default TextTransformer;
